import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Meldungen der Schutzregeln (Trigger, ERRCODE check_violation) im Klartext zeigen,
 * z. B. „Wer die Aufgabe erstellt hat, kann nicht ausgeblendet werden.“
 */
function fehlerText(error: unknown, rueckfall: string): string {
  const e = error as { code?: string; message?: string } | null;
  // Eindeutigkeit board_tasks_meeting_einmal: gleicher Titel aus demselben Meeting
  if (e?.code === '23505' && e.message?.includes('board_tasks_meeting_einmal')) {
    return 'Diese Aufgabe gibt es aus diesem Meeting schon.';
  }
  if (e?.code === '23514' && e.message?.includes('board_tasks_quelle_link_check')) {
    return 'Der Link zum Transkript muss mit https:// beginnen.';
  }
  return e?.code === '23514' && e.message ? e.message : rueckfall;
}

export interface BoardTask {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  category: string | null;
  assigned_to: string | null;
  created_by: string | null;
  sort_order: number | null;
  created_at: string;
  updated_at: string;
  due_date: string | null;
  start_date: string | null;
  area: string | null;
  linked_entity_type: string | null;
  linked_entity_id: string | null;
  linked_page_path: string | null;
  milestone: boolean | null;
  color_label: string | null;
  attachments: Json[] | null;
  /** Personen, für die die Aufgabe nicht existiert (RLS). Fehlt bei Kurs-Aufgaben (cn_). */
  ausgeblendet_fuer?: string[];
  /** Herkunft: manuell | claude | meeting | mail (Datenbank setzt claude bei Anlage ohne Anmeldung). */
  quelle?: string;
  quelle_info?: string | null;
  /** Link zur Quelle, z. B. Transkript in Google Drive (nur https). */
  quelle_link?: string | null;
  /** Projekt aus Daniels Projekt-Cockpit (cockpit_projekte.slug). */
  projekt?: string | null;
}

type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

export function useBoardTasks() {
  return useQuery({
    queryKey: ['board-tasks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('board_tasks')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as BoardTask[];
    },
  });
}

export function useCreateBoardTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (task: Partial<BoardTask> & { title: string }) => {
      const { data, error } = await supabase
        .from('board_tasks')
        .insert(task)
        .select()
        .single();
      if (error) throw error;
      return data as BoardTask;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board-tasks'] });
      toast.success('Aufgabe erstellt');
    },
    onError: (e) => toast.error(fehlerText(e, 'Fehler beim Erstellen')),
  });
}

export function useUpdateBoardTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<BoardTask> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('board_tasks')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result as BoardTask;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board-tasks'] });
    },
    onError: (e) => toast.error(fehlerText(e, 'Fehler beim Aktualisieren')),
  });
}

export function useDeleteBoardTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('board_tasks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board-tasks'] });
      toast.success('Aufgabe gelöscht');
    },
    onError: () => toast.error('Fehler beim Löschen'),
  });
}
